import { GenericHeaderNotification, SurveyPopUp } from 'uicomponents';

import { SurveyGetters } from './getters';
import { SurveyMutations } from './mutations';
import {
  QuestionOption,
  RawUserAnswer,
  Survey,
  SurveyQuestion,
  SurveyResponse,
  SurveyState,
  SurveyStatus
} from './types';
import { BroadcastSurvey } from '../event/types';

import type { RootState } from '@/store';

import analytics from '@/analytics';
import SurveyPreviewer from '@/components/Survey/SurveyPreviewer.vue';
import { LOCAL_STORAGE_KEYS } from '@/constants';
import DialogService from '@/services/dialogs/DialogService';
import loggerService from '@/services/logger.service';
import { ActivityType, handleSpeakerActivity } from '@/services/speakerActivity.service';
import {
  getSurvey,
  saveUserSurveyResponse,
  saveUserSurveyResponseOnDemand,
  updateSurveyState
} from '@/services/survey.service';

export const SurveyNotificationTimeout = 3000;
let surveyCloseTimeInterval: ReturnType<typeof setInterval> | null = null;

export interface SurveysContext {
  state: SurveyState;
  rootState: RootState;
  getters: SurveyGetters;
  rootGetters: { [k: string]: any };
  commit: <k extends keyof SurveyMutations>(mutation: k, payload?: any, config?: { root: boolean }) => any;
  dispatch: (action: string, payload?: any, root?: { root: boolean }) => Promise<any>;
}

const actions = {
  openSurvey(context: SurveysContext, surveyId: string) {
    const survey = context.state.surveys[surveyId];
    if (!survey) {
      loggerService.warn('Survey not found ', surveyId);
      return;
    }

    if (survey.surveyPopUpId && survey.surveyPopUpId !== null) {
      return;
    }

    if (context.state.isSurveyMinimized) {
      return;
    }

    const { index } = DialogService.show(
      SurveyPreviewer,
      {
        surveyId: surveyId,
        id: 'survey-popup'
      },
      {
        placement: 'bottom-right',
        hideOverlay: true,
        blockBackgroundActions: false,
        on: {}
      }
    );
    context.commit('updateSurveyPopUpId', { surveyId, popUpId: index });
  },
  openSurveyPreview(context: SurveysContext, surveyId: string) {
    if (!context.state.surveys[surveyId]) {
      loggerService.warn('Survey not found for preview ', surveyId);
      return;
    }
    context.commit('setPreviewSurveyId', surveyId);
    const { index } = DialogService.show(
      SurveyPreviewer,
      {
        surveyId: surveyId,
        id: 'survey-popup'
      },
      {
        placement: 'bottom-right',
        hideOverlay: true,
        blockBackgroundActions: false,
        on: {}
      }
    );
    context.commit('updateSurveyPopUpId', { surveyId, popUpId: index });
  },
  closeSurveyPreview(context: SurveysContext, surveyId: string) {
    context.commit('setPreviewSurveyId', null);
    const surveyPopUpId = context.state.surveys[surveyId].surveyPopUpId;
    DialogService.close(surveyPopUpId);
  },
  showPublishSurveyDialog(context: SurveysContext, surveyId: string) {
    const i18n = context.rootState.event.translator?.i18n;
    DialogService.show(
      SurveyPopUp,
      {
        title: i18n.global.t(`@@translate@@ Launch surveys will take over attendees' engagement panel.`),
        description: i18n.global.t('@@translate@@ Do you want to launch your survey now?')
      },
      {
        on: {
          onSubmit: async () => {
            context.dispatch('publishSurvey', surveyId);
          }
        }
      }
    );
  },
  async publishSurvey(context: SurveysContext, surveyId: string) {
    const currentBrodcast = context.rootGetters['event/currentBroadcast'];

    if (!currentBrodcast) {
      loggerService.info('No current broadcast found');
      return;
    }

    if (!currentBrodcast?.surveys?.find((survey: BroadcastSurvey) => survey.survey_id === surveyId)) {
      loggerService.warn('Survey not found in current broadcast');
      return;
    }

    if (
      currentBrodcast?.surveys?.find(
        (survey: BroadcastSurvey) => survey.survey_id !== surveyId && survey.survey_status === 'OPEN'
      )
    ) {
      loggerService.warn('Another survey is already open');
      return;
    }

    const params = {
      broadcast: currentBrodcast.id,
      survey_status: 'OPEN'
    };
    try {
      await updateSurveyState(surveyId, params);
      handleSpeakerActivity(() => Promise.resolve(), ActivityType.SurveyOpen, ActivityType.SurveyOpenFailed);

      analytics.track('survey_published', {
        eventId: context.rootState.route?.params?.eventID,
        broadcastId: context.rootState.route?.params?.id,
        surveyId: surveyId
      });

      showHeaderNotification(
        context,
        '@@translate@@ Success! Your Survey has been launched for attendees.',
        'tick',
        'lightgreen'
      );
    } catch (error) {
      loggerService.error('Error while publishing survey ', error);
      handleSpeakerActivity(() => Promise.resolve(), ActivityType.SurveyOpenFailed, ActivityType.SurveyOpenFailed);
      showHeaderNotification(
        context,
        '@@translate@@ Unable to publish. Please try again.',
        'error-triangle',
        'highlight'
      );
    }
  },
  unPublishSurveyDialog(context: SurveysContext, surveyId: string) {
    if (surveyId !== context.state.publishedSurveyId) {
      loggerService.warn('Survey is not published');
      return;
    }

    const i18n = context.rootState.event.translator?.i18n;
    DialogService.show(
      SurveyPopUp,
      {
        title: i18n.global.t(`@@translate@@ Closing this survey will remove it from attendees' engagement panel.`),
        description: i18n.global.t('@@translate@@ Are you sure you want to close this survey?'),
        submitButtonText: i18n.global.t('@@translate@@ Okay')
      },
      {
        on: {
          onSubmit: async () => {
            context.dispatch('unPublishSurvey', surveyId);
          }
        }
      }
    );
  },
  async unPublishSurvey(context: SurveysContext, surveyId: string) {
    const currentBrodcast = context.rootGetters['event/currentBroadcast'];

    if (!currentBrodcast) {
      loggerService.info('No current broadcast found');
      return;
    }

    if (
      currentBrodcast?.surveys?.find(
        (survey: BroadcastSurvey) => survey.survey_id === surveyId && survey.survey_status !== 'OPEN'
      )
    ) {
      return;
    }

    const params = {
      broadcast: currentBrodcast.id,
      survey_status: 'CLOSED'
    };
    try {
      await updateSurveyState(surveyId, params);
      handleSpeakerActivity(() => Promise.resolve(), ActivityType.SurveyClose, ActivityType.SurveyCloseFailed);
      analytics.track('survey_unpublished', {
        eventId: context.rootState.route?.params?.eventID,
        broadcastId: context.rootState.route?.params?.id,
        surveyId: surveyId
      });
      showHeaderNotification(
        context,
        '@@translate@@ Success! Your Survey has been closed for attendees.',
        'tick',
        'lightgreen'
      );
    } catch (error) {
      loggerService.error('Error while closing survey ', error);
      handleSpeakerActivity(() => Promise.resolve(), ActivityType.SurveyCloseFailed, ActivityType.SurveyCloseFailed);
      showHeaderNotification(
        context,
        '@@translate@@ Unable to un-publish. Please try again.',
        'error-triangle',
        'highlight'
      );
    }
  },
  async loadSurvey(
    context: SurveysContext,
    { surveyId, surveyStatus }: { surveyId: string; surveyStatus: SurveyStatus }
  ) {
    return new Promise(resolve => {
      const survey = context.state.surveys[surveyId];
      if (survey) {
        survey.surveyStatus = 'UPDATING';
        context.commit('setSurvey', survey);
      }

      getSurvey(surveyId)
        .then((res: any) => {
          const newSurvey = {} as Survey;
          newSurvey.lastQuestionAnsweredIndex = -1;
          newSurvey.questions = res.questions;
          newSurvey.title = res.title;
          newSurvey.description = res.description;
          newSurvey.timeLeftInClosingSurvey = '';
          newSurvey.surveyStatus = null;
          newSurvey.id = res.id;
          newSurvey.broadcasts = res.broadcasts;
          newSurvey.surveyStatus = surveyStatus;
          newSurvey.surveyError = null;
          newSurvey.isSurveySubmitted = false;
          newSurvey.answerMap = {};
          newSurvey.show_in_on_demand = res.show_in_on_demand;
          newSurvey.submit_message = res.submit_message;
          context.commit('setSurvey', newSurvey);
          resolve(true);
        })
        .catch((err: any) => {
          if (survey) {
            context.commit('setSurvey', survey);
          }
          loggerService.error('Error while loading survey ', err);
        });
    });
  },
  async loadSurveys(context: SurveysContext) {
    const currentBroadcast = context.rootGetters['event/currentBroadcast'];

    if (!currentBroadcast) {
      loggerService.info('No current broadcast found');
      return;
    }

    context.commit('clearSurvey');
    context.commit('updateLoadingState', true);

    context.dispatch('handleBroadcastSurveys', currentBroadcast.id).then(() => {
      context.commit('updateLoadingState', false);
    });
  },
  async submitSurvey(context: SurveysContext, surveyId: string) {
    const survey = context.state.surveys[surveyId];

    const answersMap = survey.answerMap;

    if (!survey) {
      loggerService.warn('Survey not found ', surveyId);
      return;
    }

    if (context.state.publishedSurveyId !== surveyId) {
      loggerService.warn('Survey not published ', surveyId);
      return;
    }

    context.state.surveys[surveyId].isSurveySubmitted = true;
    const isAnyRequiredMissed = checkAnyRequiredQuestionMissed(context, answersMap, surveyId);

    if (isAnyRequiredMissed) {
      showHeaderNotification(
        context,
        '@@translate@@ Please answer all required questions.',
        'error-triangle',
        'highlight'
      );
      return;
    }

    const responseMapper: SurveyResponse[] = [];

    survey.questions.forEach((question: SurveyQuestion) => {
      let answer = answersMap[question.id];

      if (question.question_type === 'MULTI') {
        answer = (answer as QuestionOption[])?.map((answer: any) => answer?.text) || [''];
      }

      if (question.question_type === 'SINGLE') {
        answer = (answer as QuestionOption)?.text || '';
      }

      if (question.question_type === 'RATING') {
        answer = !answer ? '' : answer?.toString() + ' star';
      }

      if (question.question_type === 'TEXT') {
        answer = answer?.toString() || '';
      }

      responseMapper.push({
        question_text: question?.text,
        question_type: question?.question_type,
        answer_text: answer as any
      });
    });

    const currentBroadcast = context.rootGetters['event/currentBroadcast'];

    if (isUserSubmittedSurvey(surveyId, currentBroadcast.id)) {
      loggerService.info('User already submitted survey ', surveyId);
      context.dispatch('closeAndClearSurveyState', surveyId);
      showHeaderNotification(
        context,
        '@@translate@@ You have already submitted this survey.',
        'error-triangle',
        'highlight'
      );
      return;
    }

    const finalResponseMapper = {
      survey_id: surveyId,
      broadcast_id: currentBroadcast.id,
      responses: responseMapper,
      event_id: context.rootState.route?.params?.eventID
    };

    const isOnDemand = !!context.rootState.event.event?.on_demand_mode && context.rootState.event.useOnDemandMQL;
    const oldStatusForSurvey = survey.surveyStatus;
    context.commit('updateSurveyStatus', { surveyId, status: 'SUBMITTING' });

    try {
      const saveFn = isOnDemand ? saveUserSurveyResponseOnDemand : saveUserSurveyResponse;
      const data = await saveFn(finalResponseMapper);
      saveUserSubmittedSurvey(surveyId, currentBroadcast.id);

      if (isOnDemand) {
        context.commit('updateSurveyStatus', { surveyId, status: 'SUBMITTED' });
        analytics.track('survey_submitted', {
          eventId: context.rootState.route?.params?.eventID,
          broadcastId: currentBroadcast.id,
          surveyId: surveyId,
          onDemand: true
        });
      } else {
        context.dispatch('closeAndClearSurveyState', surveyId);
        if (data.statusCode === 205) {
          showHeaderNotification(
            context,
            '@@translate@@ You have already submitted this survey.',
            'error-triangle',
            'highlight'
          );
          analytics.track('survey_submitted', {
            eventId: context.rootState.route?.params?.eventID,
            broadcastId: currentBroadcast.id,
            surveyId: surveyId,
            reSubmitted: true
          });
        } else {
          showHeaderNotification(context, '@@translate@@ Success! Thank you for your feedback.', 'tick', 'lightgreen');
          analytics.track('survey_submitted', {
            eventId: context.rootState.route?.params?.eventID,
            broadcastId: currentBroadcast.id,
            surveyId: surveyId
          });
        }
      }
    } catch (error) {
      loggerService.error('Error while submitting survey ', error);
      showHeaderNotification(
        context,
        '@@translate@@ Unable to submit. Please try again.',
        'error-triangle',
        'highlight'
      );
      context.commit('updateSurveyStatus', {
        surveyId,
        status: oldStatusForSurvey
      });
    }
  },
  minimizeSurvey(context: SurveysContext, surveyId: string) {
    const survey = context.state.surveys[surveyId];
    if (survey) {
      context.commit('updateIsMinimized', true);
      context.commit('updateSurveyPopUpId', { surveyId, popUpId: null });
    }
  },
  openSurveyAndUpdateMinimized(context: SurveysContext) {
    context.commit('updateIsMinimized', false);
    context.dispatch('openSurvey', context.state.publishedSurveyId);
  },
  handleBroadcastTransistion(context: SurveysContext, time: string) {
    const isBroadcastSpeakerOrOrganizer = context.rootGetters['event/isBroadcastSpeakerOrOrganizer'];

    if (isBroadcastSpeakerOrOrganizer) {
      return;
    }

    const publishedSurveyId = context.state.publishedSurveyId;
    if (!publishedSurveyId) {
      return;
    }

    context.dispatch('closeSurveyAndStartTimer', {
      surveyId: publishedSurveyId,
      time
    });
  },
  closeSurveyAndStartTimer(context: SurveysContext, { surveyId, time = '30' }: { surveyId: string; time: string }) {
    if (surveyCloseTimeInterval) {
      clearInterval(surveyCloseTimeInterval);
    }

    if (!context.state.surveys[surveyId]) {
      return;
    }

    function pad(v: number): string {
      if (isNaN(v)) {
        return '00:00';
      }
      return `${v}`.padStart(2, '0');
    }

    context.state.surveys[surveyId].timeLeftInClosingSurvey = pad(parseInt(time)) || '';
    context.commit('updateSurveyStatus', { surveyId, status: 'CLOSING' });

    surveyCloseTimeInterval = setInterval(() => {
      if (!context.state.surveys[surveyId] && surveyCloseTimeInterval) {
        clearInterval(surveyCloseTimeInterval);
        return;
      }

      let time = parseInt(context.state.surveys[surveyId]?.timeLeftInClosingSurvey ?? 0);

      if (time > 0) {
        time = time - 1;
      }

      context.state.surveys[surveyId].timeLeftInClosingSurvey = pad(time);

      if (time <= 0 && surveyCloseTimeInterval) {
        context.dispatch('closeAndClearSurveyState', surveyId);
        clearInterval(surveyCloseTimeInterval);
      }
    }, 1000);
  },
  closeAndClearSurveyState(context: SurveysContext, surveyId: string) {
    if (context.state.previewSurveyId === surveyId) {
      context.commit('setPreviewSurveyId', null);
    }
    DialogService.close(context.state.surveys[surveyId].surveyPopUpId);
    context.state.surveys[surveyId].timeLeftInClosingSurvey = '';
    context.commit('updateSurveyPopUpId', { surveyId, popUpId: null });
    context.commit('updateIsMinimized', false);
    context.commit('setPublishedSurveyId', null);
    context.commit('updateSurveyStatus', { surveyId, status: null });
  },
  onAnswer(
    context: SurveysContext,
    {
      surveyId,
      question,
      answer,
      questionIndex
    }: {
      surveyId: string;
      question: SurveyQuestion;
      answer: RawUserAnswer;
      questionIndex: number;
    }
  ) {
    const isBroadcastSpeakerOrOrganizer = context.rootGetters['event/isBroadcastSpeakerOrOrganizer'];

    if (isBroadcastSpeakerOrOrganizer) {
      return;
    }

    const { question_type, id } = question;
    const answerMap = context.state.surveys?.[surveyId]?.answerMap;

    context.commit('lastQuestionAnsweredIndex', {
      surveyId,
      index: questionIndex
    });

    switch (question_type) {
      case 'SINGLE': {
        const oldSingleAnswer = answerMap?.[id] as QuestionOption;
        if (!oldSingleAnswer || oldSingleAnswer.id !== (answer as QuestionOption)?.id) {
          context.commit('setSurveyAnswer', {
            surveyId,
            questionId: id,
            answer
          });
        } else {
          context.commit('removeSurveyAnswer', { surveyId, questionId: id });
        }
        break;
      }

      case 'MULTI': {
        const multiAnswers = (answerMap?.[id] || []) as QuestionOption[];
        const newAnswer = answer as QuestionOption;

        if (multiAnswers.some(ans => ans?.id === newAnswer?.id)) {
          multiAnswers.splice(
            multiAnswers.findIndex(ans => ans.id === newAnswer.id),
            1
          );
        } else {
          multiAnswers.push(newAnswer);
        }

        if (multiAnswers.length === 0) {
          context.commit('removeSurveyAnswer', { surveyId, questionId: id });
        } else {
          context.commit('setSurveyAnswer', {
            surveyId,
            questionId: id,
            answer: multiAnswers
          });
        }
        break;
      }

      case 'RATING': {
        const oldRateAnswer = answerMap?.[id];
        if (oldRateAnswer === answer) {
          context.commit('removeSurveyAnswer', { surveyId, questionId: id });
        } else {
          context.commit('setSurveyAnswer', {
            surveyId,
            questionId: id,
            answer
          });
        }
        break;
      }

      case 'TEXT': {
        context.commit('setSurveyAnswer', { surveyId, questionId: id, answer });
        break;
      }
      default: {
        loggerService.error('Error while saving answer for questionType: ', question_type);
      }
    }
  },
  async handleBroadcastSurveys(context: SurveysContext, broadcastId: string) {
    const currentBroadcast = context.rootGetters['event/currentBroadcast'];

    if (currentBroadcast.id !== broadcastId) {
      return;
    }

    const shouldHandleActions = !currentBroadcast?.is_automated_run_of_show;

    const existingSurveys = Object.values(context.state.surveys);
    context.commit('updateLoadingState', true);
    for (let i = 0; i < currentBroadcast.surveys?.length; i++) {
      if (existingSurveys.find((survey: Survey) => survey.id === currentBroadcast.surveys[i].survey_id)) {
        continue;
      }
      await context.dispatch('loadSurvey', {
        surveyId: currentBroadcast.surveys[i].survey_id,
        surveyStatus: currentBroadcast.surveys[i].survey_status
      });
    }

    for (let i = 0; i < existingSurveys.length; i++) {
      const existingSurvey = existingSurveys[i];
      if (!currentBroadcast.surveys.find((survey: BroadcastSurvey) => survey.survey_id === existingSurvey.id)) {
        loggerService.warn('Survey removed from broadcast so closing it now ', existingSurvey.id);
        if (existingSurvey.id === context.state.publishedSurveyId) {
          context.commit('setPublishedSurveyId', null);
        }
        DialogService.close(existingSurvey.surveyPopUpId);
        context.commit('deleteSurvey', existingSurvey.id);
      }
    }

    const currentOpenSurvey = currentBroadcast?.surveys?.find(
      (survey: BroadcastSurvey) => survey.survey_status === 'OPEN'
    );
    context.commit('updateLoadingState', false);

    const isBroadcastSpeakerOrOrganizer = context.rootGetters['event/isBroadcastSpeakerOrOrganizer'];
    const isOnBroadcastPage = context.rootState.route.params.id === broadcastId;
    if (currentOpenSurvey) {
      if (
        !isBroadcastSpeakerOrOrganizer &&
        (!context.rootState.event.event?.on_demand_mode || context.rootState.event.useOnDemandMQL) &&
        !context.rootState.event?.stage?.isTVMode &&
        !isUserSubmittedSurvey(currentOpenSurvey.survey_id, currentBroadcast.id) &&
        isOnBroadcastPage
      ) {
        context.dispatch('openSurvey', currentOpenSurvey.survey_id);
      }
      context.commit('setPublishedSurveyId', currentOpenSurvey.survey_id);
    }

    if (!currentOpenSurvey && context.state.publishedSurveyId && shouldHandleActions) {
      context.dispatch('closeSurveyAndStartTimer', {
        surveyId: context.state.publishedSurveyId,
        time: '30'
      });
    }
  },
  async handleSurveyUpdation(
    context: SurveysContext,
    params: {
      broadcast: string;
      id: string;
    }
  ) {
    const currentBroadcast = context.rootGetters['event/currentBroadcast'];
    if (currentBroadcast.id !== params.broadcast) {
      return;
    }

    const survey = context.state.surveys[params.id];

    await context.dispatch('loadSurvey', {
      surveyId: params.id,
      surveyStatus: survey?.surveyStatus
    });
  }
};

export function showHeaderNotification(context: SurveysContext, text: string, iconName: string, iconColor: string) {
  const i18n = context.rootState.event.translator?.i18n;

  DialogService.show(
    GenericHeaderNotification,
    {
      text: i18n.global.t(text),
      iconName,
      iconColor
    },
    {
      hideOverlay: true,
      cancelOnClickOutside: false,
      blockBackgroundActions: false,
      closeOnCTAClick: false,
      autoCancelTimeout: SurveyNotificationTimeout
    }
  );
}

export function checkAnyRequiredQuestionMissed(context: SurveysContext, answersMap: any, surveyId: string) {
  const survey = context.state.surveys[surveyId];

  if (!survey) {
    loggerService.warn('Survey not found ', surveyId);
    return;
  }

  for (let i = 0; i < survey.questions.length; i++) {
    const question = survey.questions[i];
    const answer = answersMap[question.id];

    if (!answer && question?.required) {
      loggerService.warn('Answer is required for this question ', question.id);
      return true;
    }
  }

  return false;
}

export function isUserSubmittedSurvey(surveyId: string, broadcastId: string) {
  const isUserSubmittedSurvey = window?.sessionStorage.getItem(
    `${broadcastId}__${surveyId}${LOCAL_STORAGE_KEYS.userSurveySubmissionKey}`
  );

  if (isUserSubmittedSurvey) {
    return true;
  }

  return false;
}

export function saveUserSubmittedSurvey(surveyId: string, broadcastId: string) {
  window?.sessionStorage.setItem(`${broadcastId}__${surveyId}${LOCAL_STORAGE_KEYS.userSurveySubmissionKey}`, 'true');
}

export default actions;
